import { type DataTableColumnDef } from 'components/data-table';
import { PlainLink } from 'components/link';
import {
  NameWithStatusTag,
  TypeCurveStatusTag,
} from 'components/lifecycle-status-tag';
import {
  TypeCurveVerificationStatusIndicator,
  typeCurveVerificationIsDecided,
} from 'components/type-curve-verification-status/type-curve-verification-status';
import { TypeCurveValidationCell } from 'components/type-curve-validation-status/type-curve-validation-status';
import { formatNumber } from 'utils/number';
import { parseLocalDateTime } from 'utils/dates';
import { cleanStatus } from './utils';
import type { TypeCurveListingRow } from 'types/api-responses';
import styles from './type-curves-listing.module.scss';

type TcRow = TypeCurveListingRow & Record<string, unknown>;

export const typeCurvesListingColumns: DataTableColumnDef<TcRow>[] = [
  {
    accessorKey: 'typeCurveScenario',
    header: 'Type Curve Scenario',
    size: 180,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ row, getValue }) => {
      const name = (getValue() as string | null) ?? '--';
      const href = row.original.resources?.typeCurveScenario?.href;
      if (!href) return name;
      return (
        <PlainLink href={href} onClick={(e) => e.stopPropagation()}>
          {name}
        </PlainLink>
      );
    },
  },
  {
    accessorKey: 'uniqueTcId',
    header: 'Unique TC ID',
    size: 500,
    enableSorting: true,
    cell: ({ row, getValue }) => {
      // Prefer the server-built href; `/type_curves/:public_id` is the canonical
      // detail URL, so the fallback matches it when resources are absent.
      const href =
        row.original.resources?.typeCurve?.href ??
        row.original.resources?.typeCurveHeader?.href ??
        `/type_curves/${row.original.tcPublicId}`;
      // Every row in this view is a family's current promoted version (Query::TypeCurveHeaders::
      // DetailsElastic), so the tag only varies between Promoted, Default and Archived.
      return (
        <NameWithStatusTag
          tag={
            <TypeCurveStatusTag
              promoted
              isDefault={row.original.isDefault}
              archived={row.original.archived}
            />
          }
        >
          <PlainLink href={href} onClick={(e) => e.stopPropagation()}>
            {getValue() as string}
          </PlainLink>
        </NameWithStatusTag>
      );
    },
  },
  {
    accessorKey: 'verification',
    header: 'Verification',
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => {
      const status = getValue() as string | null | undefined;
      if (!typeCurveVerificationIsDecided(status)) return '--';
      return (
        <TypeCurveVerificationStatusIndicator
          status={status}
          showLabel
          className={styles.verificationStatusCell}
          iconClasses={{
            correct: styles.verificationStatusCorrect,
            incorrect: styles.verificationStatusIncorrect,
          }}
        />
      );
    },
  },
  {
    accessorKey: 'validation',
    header: 'Validation',
    size: 150,
    cell: ({ row }) => (
      <TypeCurveValidationCell
        id={row.original.id}
        validity={row.original.validation}
      />
    ),
  },
  {
    accessorKey: 'lastVerifiedBy',
    header: 'Last Verified By',
    size: 180,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '--',
  },
  {
    accessorKey: 'typeCurveArea',
    header: 'Type Curve Area',
    size: 180,
    enableSorting: true,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'basin',
    header: 'Basin',
    size: 120,
    enableSorting: true,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'formation',
    header: 'Formation',
    size: 220,
    enableSorting: true,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    enableSorting: true,
    cell: ({ getValue }) => cleanStatus(getValue() as string),
  },
  {
    accessorKey: 'promotedBy',
    header: 'Promoted By',
    size: 150,
    enableSorting: false,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '—',
  },
  {
    accessorKey: 'isDefault',
    header: 'Default',
    size: 100,
    enableSorting: true,
    meta: { type: 'enum' },
    // Only the default is worth calling out. Rendering "No" on every other row would put a column of
    // noise beside the one fact that matters, so the negative case stays blank.
    cell: ({ getValue }) => ((getValue() as boolean | null) ? 'Default' : '—'),
  },
  {
    accessorKey: 'archived',
    header: 'Archived',
    size: 100,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ getValue }) => ((getValue() as boolean) ? 'Yes' : 'No'),
  },
  {
    accessorKey: 'isDefault',
    header: 'Default',
    size: 100,
    enableSorting: true,
    meta: { type: 'enum' },
    // Only the default is worth calling out. Rendering "No" on every other row would put a column of
    // noise beside the one fact that matters, so the negative case stays blank.
    cell: ({ getValue }) => ((getValue() as boolean | null) ? 'Default' : '—'),
  },
  {
    accessorKey: 'state',
    header: 'State',
    size: 80,
    enableSorting: true,
    meta: { type: 'enum' },
  },
  {
    accessorKey: 'wellCount',
    header: 'Well Count',
    size: 120,
    enableSorting: true,
    meta: { type: 'integer', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'wellSpacingFt',
    header: 'Well Spacing (ft)',
    size: 150,
    enableSorting: true,
    meta: { type: 'decimal', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'oilEurMbbl30yr',
    header: 'Oil EUR (Mbbl 30yr)',
    size: 170,
    enableSorting: true,
    meta: { type: 'decimal', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'gasEurMmcf30yr',
    header: 'Gas EUR (MMcf 30yr)',
    size: 180,
    enableSorting: true,
    meta: { type: 'decimal', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'waterEurMbbl30yr',
    header: 'Water EUR (Mbbl 30yr)',
    size: 190,
    enableSorting: true,
    meta: { type: 'decimal', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'sectionEurMboe20',
    header: 'Section EUR (Mboe20)',
    size: 190,
    enableSorting: true,
    meta: { type: 'decimal', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 2, 2, 'decimal', true, '--'),
  },
  {
    accessorKey: 'lastVerifiedDate',
    id: 'lastVerificationDate',
    header: 'Last Verification Date',
    size: 180,
    enableSorting: true,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '--';
    },
  },
  {
    accessorKey: 'newWellsSinceCreatedCount',
    header: 'New Wells Since Created',
    size: 200,
    enableSorting: true,
    meta: { type: 'integer', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'newWellsSinceLastQcCount',
    header: 'New Wells Since Last QC',
    size: 200,
    enableSorting: true,
    meta: { type: 'integer', align: 'left' },
    cell: ({ getValue }) =>
      formatNumber(getValue() as number, 0, 0, 'decimal', true, '--'),
  },
  {
    accessorKey: 'dataLoadSource',
    header: 'Data Source',
    size: 140,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '--',
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 140,
    enableSorting: true,
    meta: { type: 'enum' },
    cell: ({ getValue }) => (getValue() as string | null) ?? '--',
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 140,
    enableSorting: true,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '--';
    },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 140,
    enableSorting: true,
    meta: { type: 'date' },
    cell: ({ getValue }) => {
      const value = getValue() as string | null;
      return value ? parseLocalDateTime(value) : '--';
    },
  },
];
